import type { ReactNode } from 'react'
import type { Locale } from '@/constants/i18n'
import type { CityKey } from '@/constants/cities'
import type { Currency } from '@/lib/format'
import type { Messages } from '@/i18n/messages'
import type { PluralForms } from '@/i18n/plural'

import { createContext, useContext, useMemo } from 'react'
import { localeDir } from '@/constants/i18n'
import { CITY_LABELS } from '@/constants/cities'
import { formatMoney } from '@/lib/format'
import { formatDateLocalized } from '@/lib/dates'
import { dictionaries } from '@/i18n/messages'
import { fmt, plural } from '@/i18n/plural'

interface I18nValue {
    locale: Locale
    dir: 'rtl' | 'ltr'
    m: Messages
    fmt: typeof fmt
    count: (forms: PluralForms, n: number, values?: Record<string, string | number>) => string
    date: (iso: string) => string
    money: (amount: number, currency: Currency) => string
    city: (key: CityKey | string) => string
}

const I18nContext = createContext<I18nValue | null>(null)

export const buildI18n = (locale: Locale): I18nValue => {
    const m = dictionaries[locale]

    return {
        locale,
        dir: localeDir(locale),
        m,
        fmt,
        count: (forms, n, values) => plural(locale, forms, n, values),
        date: iso => formatDateLocalized(iso, m.calendar.monthsShort, m.calendar.datePattern),
        money: (amount, currency) => formatMoney(amount, currency, locale),
        city: key => (key in CITY_LABELS ? CITY_LABELS[key as CityKey][locale] : String(key))
    }
}

export const I18nProvider = ({ locale, children }: { locale: Locale, children: ReactNode }): ReactNode => {
    const value = useMemo(() => buildI18n(locale), [locale])

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    )
}

export const useI18n = (): I18nValue => {
    const value = useContext(I18nContext)

    return value ?? buildI18n('en')
}
